import { router } from '@Routes/__root';
import { useBoundStore } from '@Store/index.ts';
import { routesModel } from '@Router/router.consts.ts';
import { useEffect, useState } from 'react';
import { useLocation, useSearch } from '@tanstack/react-router';
import { RoutesMap } from '@Router/router.types.ts';
// import { isAllowedPrev } from '../hooks/custom/isAllowedPrev.service';

import type { NavigateOptions } from '@tanstack/react-router';
import { isAllowedPrev } from '@Hooks/custom/isAllowedPrev.service';

const ROUTES_TO_AVOID_NEXT: string[] = [routesModel[RoutesMap.BUSINESS_TYPE_SELECTION].path];

export const ROUTES_TO_AVOID_BACK = [
  routesModel[RoutesMap.REVIEW].path,
  routesModel[RoutesMap.DEBIT_CARD_NAME].path,
  routesModel[RoutesMap.SOCURE_DOC_V].path,
  routesModel[RoutesMap.UNDER_REVIEW].path,
  routesModel[RoutesMap.GENERIC_MESSAGE_PAGE].path,
  routesModel[RoutesMap.INVITE_ADMIN_FORM].path,
  routesModel[RoutesMap.INVITE_ACCOUNTANT_FORM].path,
  routesModel[RoutesMap.INVITE_TEAM_MEMBER_INTRO].path,
  routesModel[RoutesMap.INVITE_TEAM_MEMBER_FINISHED].path,
  routesModel[RoutesMap.VERIFY_PHONE_NUMBER].path,
  routesModel[RoutesMap.SET_PASSWORD].path,
];

const ROUTES_TO_HIDE_NAVIGATION: string[] = [routesModel[RoutesMap.PLAN_SELECTION].path];
const ROUTES_TO_FORCE_SHOW_BACK: string[] = [routesModel[RoutesMap.ADD_OWNER].path];

export const useNavigationManager = () => {
  const { getOnboardingData, currentPageIndex, editMode, setEditMode } = useBoundStore(
    (state) => state.onboardingSlice
  );
  const [isAllowedNextPage, setIsAllowedNextPage] = useState(true);
  const [isAllowedPrevPage, setIsAllowedPrevPage] = useState(true);
  const [isNavigationHidden, setIsNavigationHidden] = useState(false);
  const { pathname } = useLocation();
  const search = useSearch({ strict: false });

  useEffect(() => {
    setIsAllowedNextPage(!ROUTES_TO_AVOID_NEXT.includes(pathname));
    setIsNavigationHidden(ROUTES_TO_HIDE_NAVIGATION.includes(pathname));
    setIsAllowedPrevPage(
      isAllowedPrev({
        editMode,
        currentPageIndex,
        pathname,
        ROUTES_TO_AVOID_BACK,
        forceShowBack: ROUTES_TO_FORCE_SHOW_BACK.includes(pathname),
      })
    );
  }, [currentPageIndex, pathname, editMode]);

  const navigateToFirstPage = () => {
    const { navigationStack } = getOnboardingData();
    const pageId = navigationStack[0];
    router.navigate({ to: routesModel[pageId].path, search: { ...search } });
  };

  const navigateToNextPage = () => {
    if (editMode) {
      router.navigate({ to: routesModel[RoutesMap.REVIEW].path });
      setEditMode(false);
    } else {
      const { navigationStack } = getOnboardingData();
      const nextPageId = navigationStack[currentPageIndex + 1];
      router.navigate({ to: routesModel[nextPageId].path });
    }
  };

  const navigateToPreviousPage = () => {
    const { navigationStack } = getOnboardingData();
    const previousPageId = navigationStack[currentPageIndex - 1];
    router.navigate({ to: routesModel[previousPageId].path });
  };

  const navigateToSpecificPage = <TPageId extends RoutesMap>(
    pageId: TPageId,
    qParams?: NavigateOptions<typeof router>['search']
  ) => {
    const to = routesModel[pageId].path;
    const route = {
      to,
    } as NavigateOptions<typeof router>; //TODO: check if satisfies can work here
    if (qParams) {
      route.search = qParams;
    }
    router.navigate(route);
  };

  return {
    navigateToFirstPage,
    navigateToNextPage,
    navigateToPreviousPage,
    isAllowedNextPage,
    isAllowedPrevPage,
    isNavigationHidden,
    navigateToSpecificPage,
  };
};
